import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "../components/Icons";
import TimesheetDropdown from "../components/TimesheetDropdown";
import Pagination from "../components/Pagination";
import EmployeeTargetsAppraisalsView from "../components/EmployeeTargetsAppraisalsView";
import AddTaskModal from "../components/AddTaskModal";
import { useAuth } from "../context/AuthContext";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import "../styles/EmployeeDashboard.css";

/* ─── helpers ─── */
const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
};
const fmtHours = (h) => {
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return `${String(hrs).padStart(2, "0")}h ${String(mins).padStart(2, "0")}m`;
};
const token = () => sessionStorage.getItem("jwtToken");
const authHdr = () => ({ Authorization: `Bearer ${token()}` });

/* ─── NAV ─── */
const NAV = [
  { key: "dashboard", label: "Dashboard", icon: "Home" },
  { key: "timesheets", label: "Timesheets", icon: "FileText" },
  { key: "leave", label: "Leave Management", icon: "Calendar" },
  { key: "appraisals", label: "My Tasks & Targets", icon: "Target" },
  { key: "reports", label: "Reports", icon: "BarChart" },
  { key: "feedback", label: "Feedback", icon: "MessageSquare" },
  { key: "settings", label: "Settings", icon: "Badge" },
];

// const FEATURES = [
//   { icon:"FileText",    color:"#EFF6FF", iconColor:"#2563EB", title:"Smart Timesheets",    desc:"Easy logging with auto time tracking" },
//   { icon:"CheckCircle", color:"#ECFDF5", iconColor:"#10B981", title:"Real-time Approvals", desc:"Instant notifications and quick approvals" },
//   { icon:"BarChart",    color:"#F5F3FF", iconColor:"#8B5CF6", title:"Detailed Reports",    desc:"Insights to boost your productivity" },
//   { icon:"Lock",        color:"#FFF7ED", iconColor:"#F59E0B", title:"Secure & Reliable",   desc:"Your data is safe with us" },
// ];

const TABS = ["Recent", "Pending", "Approved", "Rejected"];
const TAB_STATUS = { Recent: null, Pending: ["Pending", "Submitted"], Approved: ["Approved"], Rejected: ["Rejected"] };

const LEAVE_TYPES = [
  { value: "sick", label: "Sick Leave", color: "#ef4444" },
  { value: "casual", label: "Casual Leave", color: "#f59e0b" },
  { value: "annual", label: "Annual Leave", color: "#3b82f6" },
  { value: "maternity", label: "Maternity/Paternity", color: "#8b5cf6" },
  { value: "unpaid", label: "Unpaid Leave", color: "#6b7280" },
  { value: "other", label: "Other", color: "#10b981" },
];

/* ─── CircularProgress ─── */
const CircularProgress = ({ pct }) => {
  const r = 32, cx = 40, cy = 40, stroke = 6, circ = 2 * Math.PI * r, dash = (pct / 100) * circ;
  return (
    <div className="emp-progress-circle">
      <svg width="80" height="80" viewBox="0 0 80 80">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F1F5F9" strokeWidth={stroke} />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#2563EB" strokeWidth={stroke}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
      </svg>
      <div className="emp-progress-circle-text">{pct}%</div>
    </div>
  );
};

/* ══════════════════════════════════════════
   SUB-VIEWS
══════════════════════════════════════════ */

/* ── Timesheets view ── */
const TimesheetsView = ({ sundays, submittedTimesheets, username, startDate, approvedLeaveDates, holidayDates, fetchSubmittedTimesheets, clientNotesEnabled }) => {
  const [activeTab, setActiveTab] = useState("Recent");
  const [searchQuery, setSearchQuery] = useState("");
  const [openWeek, setOpenWeek] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const tabFilter = TAB_STATUS[activeTab];
  const filtered = sundays.filter(w => {
    const status = submittedTimesheets[w]?.status || "Not Submitted";
    if (tabFilter && !tabFilter.includes(status)) return false;
    if (searchQuery) {
      const fmt = dayjs(w).format("MMM D, YYYY").toLowerCase();
      return w.includes(searchQuery) || fmt.includes(searchQuery.toLowerCase());
    }
    return true;
  });
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paged = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="emp-ts-section" style={{ margin: "0 0 20px" }}>
      <div className="emp-ts-header">
        <h3 className="emp-ts-title">Timesheet Overview</h3>
        <div className="emp-ts-tabs" role="tablist">
          {TABS.map(tab => (
            <button key={tab} role="tab" aria-selected={activeTab === tab}
              className={`emp-ts-tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => { setActiveTab(tab); setCurrentPage(1); setOpenWeek(null); }}>{tab}</button>
          ))}
        </div>
      </div>
      <div className="emp-ts-toolbar">
        <div className="emp-ts-search">
          <Icon.Search size={13} color="#94A3B8" />
          <input placeholder="Search week..." value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            aria-label="Search timesheets" />
        </div>
      </div>
      <table className="emp-ts-table">
        <thead><tr><th>Week Start</th><th>Week End</th><th>Status</th><th>Total Hours</th><th>Submitted On</th><th>Actions</th></tr></thead>
        <tbody>
          {paged.length === 0
            ? <tr><td colSpan={6} className="emp-ts-empty">No timesheets found.</td></tr>
            : paged.map(week => {
              const ts = submittedTimesheets[week];
              const status = ts?.status || "Not Submitted";
              const statusKey = status.toLowerCase().replace(/\s+/g, "-");
              const isOpen = openWeek === week;
              return (
                <React.Fragment key={week}>
                  <tr style={{ background: isOpen ? "#FAFBFF" : undefined }}>
                    <td className="emp-ts-date">{dayjs(week).format("MMM D, YYYY")}</td>
                    <td className="emp-ts-date">{dayjs(week).add(6, "day").format("MMM D, YYYY")}</td>
                    <td><span className={`emp-status-pill ${statusKey}`}>{status}</span></td>
                    <td className="emp-hours-val">{ts?.totalHours != null ? `${ts.totalHours}h 00m` : "–"}</td>
                    <td className="emp-submitted-at">{ts?.submittedAt ? dayjs(ts.submittedAt).format("MMM D, YYYY") : "–"}</td>
                    <td>
                      <div className="emp-action-cell">
                        <button className="emp-icon-btn" onClick={() => setOpenWeek(isOpen ? null : week)} aria-label="View">
                          <Icon.Eye size={13} color="currentColor" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr key={`${week}-exp`} className="emp-ts-expanded">
                      <td colSpan={6}>
                        <TimesheetDropdown weekStart={week} existingData={ts} username={username}
                          startDate={startDate} approvedLeaveDates={approvedLeaveDates} holidayDates={holidayDates}
                          clientNotesEnabled={clientNotesEnabled}
                          fetchSubmittedTimesheets={fetchSubmittedTimesheets} autoOpen={true}
                          onClose={() => setOpenWeek(null)} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
        </tbody>
      </table>
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={p => { setCurrentPage(p); setOpenWeek(null); }} />
    </div>
  );
};

/* ── Approvals view — timesheets needing action (Pending / Rejected) ── */
const ApprovalsView = ({ sundays, submittedTimesheets, username, startDate, approvedLeaveDates, holidayDates, fetchSubmittedTimesheets, clientNotesEnabled }) => {
  const [openWeek, setOpenWeek] = useState(null);
  const pending = sundays.filter(w => ["Pending", "Submitted"].includes(submittedTimesheets[w]?.status));
  const rejected = sundays.filter(w => submittedTimesheets[w]?.status === "Rejected");
  const rows = [...pending, ...rejected];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div className="emp-ts-section">
        <div className="emp-ts-header" style={{ paddingBottom: 0 }}>
          <h3 className="emp-ts-title">Approvals</h3>
        </div>
        <div style={{ padding: "10px 20px 8px", borderBottom: "1px solid #F1F5F9" }}>
          <span style={{ fontSize: "0.8rem", color: "#64748B" }}>
            Timesheets awaiting approval or needing resubmission
          </span>
        </div>
        {rows.length === 0 ? (
          <div className="emp-ts-empty" style={{ padding: "48px 20px", textAlign: "center" }}>
            <Icon.CheckCircle size={32} color="#10B981" strokeWidth={1.5} />
            <p style={{ marginTop: 10, color: "#64748B", fontSize: "0.875rem" }}>All timesheets are up to date — nothing needs action.</p>
          </div>
        ) : (
          <table className="emp-ts-table">
            <thead><tr><th>Week</th><th>Status</th><th>Total Hours</th><th>Action Needed</th><th>View</th></tr></thead>
            <tbody>
              {rows.map(week => {
                const ts = submittedTimesheets[week];
                const status = ts?.status || "Not Submitted";
                const statusKey = status.toLowerCase().replace(/\s+/g, "-");
                const isOpen = openWeek === week;
                const action = status === "Rejected" ? "Resubmit required" : "Awaiting approval";
                return (
                  <React.Fragment key={week}>
                    <tr style={{ background: isOpen ? "#FAFBFF" : undefined }}>
                      <td className="emp-ts-date">{dayjs(week).format("MMM D")} – {dayjs(week).add(6, "day").format("MMM D, YYYY")}</td>
                      <td><span className={`emp-status-pill ${statusKey}`}>{status}</span></td>
                      <td className="emp-hours-val">{ts?.totalHours != null ? `${ts.totalHours}h` : "–"}</td>
                      <td style={{ fontSize: "0.78rem", color: status === "Rejected" ? "#EF4444" : "#B45309", fontWeight: 600 }}>{action}</td>
                      <td>
                        <button className="emp-icon-btn" onClick={() => setOpenWeek(isOpen ? null : week)} aria-label="View">
                          <Icon.Eye size={13} color="currentColor" />
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr key={`${week}-exp`} className="emp-ts-expanded">
                        <td colSpan={5}>
                          <TimesheetDropdown weekStart={week} existingData={ts} username={username}
                            startDate={startDate} approvedLeaveDates={approvedLeaveDates} holidayDates={holidayDates}
                            clientNotesEnabled={clientNotesEnabled}
                            fetchSubmittedTimesheets={fetchSubmittedTimesheets} autoOpen={true}
                            onClose={() => setOpenWeek(null)} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/* ── Leave Management view ── */
const LeaveView = ({ username, startDate, onApplyLeave, approvedLeaveDates }) => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!username) return;
    (async () => {
      try {
        const res = await fetch(`/timesheets/leaves/user/${username}`, { headers: authHdr() });
        if (res.ok) setLeaves(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [username]);

  const STATUS_COLORS = { APPROVED: "#10B981", PENDING: "#F59E0B", REJECTED: "#EF4444" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div className="emp-ts-section">
        <div className="emp-ts-header" style={{ paddingBottom: 0 }}>
          <h3 className="emp-ts-title">Leave Management</h3>
          <button className="emp-hero-btn-primary" style={{ marginLeft: "auto", marginBottom: 4 }}
            onClick={onApplyLeave}>
            Apply Leave
          </button>
        </div>
        <div style={{ padding: "10px 20px 8px", borderBottom: "1px solid #F1F5F9" }}>
          <span style={{ fontSize: "0.8rem", color: "#64748B" }}>Your leave history and status</span>
        </div>
        {loading ? (
          <div className="emp-ts-empty">Loading...</div>
        ) : leaves.length === 0 ? (
          <div className="emp-ts-empty" style={{ padding: "48px 20px", textAlign: "center" }}>
            <Icon.Calendar size={32} color="#FDE68A" strokeWidth={1.5} />
            <p style={{ marginTop: 10, color: "#64748B", fontSize: "0.875rem" }}>No leave requests yet. Apply for leave above.</p>
          </div>
        ) : (
          <table className="emp-ts-table">
            <thead><tr><th>Type</th><th>From</th><th>To</th><th>Taken</th><th>Reason</th><th>Status</th><th>Applied On</th><th>Rejection Reason</th></tr></thead>
            <tbody>
              {leaves.slice().reverse().map((l, i) => {
                const days = dayjs(l.endDate).diff(dayjs(l.startDate), "day") + 1;
                const color = STATUS_COLORS[l.status] || "#94A3B8";
                return (
                  <tr key={l.id || i}>
                    <td style={{ fontWeight: 600, fontSize: "0.84rem", textTransform: "capitalize" }}>{l.leaveType}</td>
                    <td className="emp-ts-date">{dayjs(l.startDate).format("MMM D, YYYY")}</td>
                    <td className="emp-ts-date">{dayjs(l.endDate).format("MMM D, YYYY")}</td>
                    <td className="emp-hours-val">{days}d</td>
                    <td style={{ fontSize: "0.78rem", color: "#475569", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.reason || "—"}</td>
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 999, fontSize: "0.72rem", fontWeight: 700, background: `${color}15`, color, border: `1px solid ${color}40` }}>
                        {l.status}
                      </span>
                    </td>
                    <td className="emp-submitted-at">{l.appliedAt ? dayjs(l.appliedAt).format("MMM D, YYYY") : "—"}</td>
                    <td style={{ fontSize: "0.78rem", maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {l.status === "REJECTED" && l.rejectionReason
                        ? <span style={{ color: "#DC2626", fontWeight: 500 }} title={l.rejectionReason}>{l.rejectionReason}</span>
                        : <span style={{ color: "#94A3B8" }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/* ── Reports view ── */
const ReportsView = ({ sundays, submittedTimesheets }) => {
  const tsValues = Object.values(submittedTimesheets);
  const approved = tsValues.filter(t => t.status === "Approved");
  const pending = tsValues.filter(t => t.status === "Pending" || t.status === "Submitted");
  const rejected = tsValues.filter(t => t.status === "Rejected");
  const totalHours = tsValues.reduce((s, t) => s + Number(t.totalHours || 0), 0);
  const approvedHours = approved.reduce((s, t) => s + Number(t.totalHours || 0), 0);

  /* Monthly breakdown by status (last 12 months) */
  const now = dayjs();
  const monthlyStatusData = [];
  for (let i = 11; i >= 0; i--) {
    const m = now.subtract(i, "month").startOf("month");
    const mStart = m.format("YYYY-MM-DD");
    const mEnd = m.endOf("month").format("YYYY-MM-DD");
    const monthTs = tsValues.filter(t => {
      const ws = t.weekStart;
      const we = t.weekEnd || (ws ? dayjs(ws).add(6, "day").format("YYYY-MM-DD") : null);
      return ws && we && ws <= mEnd && we >= mStart;
    });
    const getHoursInMonthForStatus = (statusList) => {
      let total = 0;
      monthTs.filter(t => statusList.includes(t.status)).forEach(t => {
        const start = dayjs(t.weekStart);
        const daysOfWeek = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
        daysOfWeek.forEach((day, index) => {
          const dayDate = start.add(index, "day");
          const dateStr = dayDate.format("YYYY-MM-DD");
          if (dateStr >= mStart && dateStr <= mEnd) {
            total += Number(t.hoursPerDay?.[day] || 0);
          }
        });
      });
      return total;
    };
    const data = {
      label: m.format("MMM 'YY"),
      approvedHrs: getHoursInMonthForStatus(["Approved"]),
      pendingHrs: getHoursInMonthForStatus(["Pending", "Submitted"]),
      rejectedHrs: getHoursInMonthForStatus(["Rejected"]),
    };
    if (data.approvedHrs + data.pendingHrs + data.rejectedHrs > 0) monthlyStatusData.push(data);
  }
  const maxMonthHrs = Math.max(...monthlyStatusData.map(d => d.approvedHrs + d.pendingHrs + d.rejectedHrs), 1);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12 }}>
        <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 12, padding: "14px 16px" }}>
          <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#1E40AF" }}>{totalHours}h</div>
          <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#2563EB" }}>Total Hours Logged</div>
        </div>
        <div style={{ background: "#ECFDF5", border: "1px solid #A7F3D0", borderRadius: 12, padding: "14px 16px" }}>
          <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#065F46" }}>{approved.length}</div>
          <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#047857" }}>Timesheets Approved</div>
        </div>
        <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 12, padding: "14px 16px" }}>
          <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#92400E" }}>{pending.length}</div>
          <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#B45309" }}>Awaiting Approval</div>
        </div>
        <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 12, padding: "14px 16px" }}>
          <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#991B1B" }}>{rejected.length}</div>
          <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#DC2626" }}>Rejected / Resubmit</div>
        </div>
      </div>

      {/* Monthly stacked bar chart */}
      <div style={{ background: "#fff", border: "1px solid #E2EEFF", borderRadius: 14, padding: "20px 24px", boxShadow: "0 1px 4px rgba(37,99,235,0.06)" }}>
        <h4 style={{ fontSize: "0.9375rem", fontWeight: 700, color: "#0F172A", marginBottom: 12 }}>Monthly Hours by Status</h4>
        <div style={{ display: "flex", gap: 8, marginBottom: 14, fontSize: "0.72rem" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: "#10B981", display: "inline-block" }} /> Approved</span>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: "#F59E0B", display: "inline-block" }} /> Pending</span>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: "#EF4444", display: "inline-block" }} /> Rejected</span>
        </div>
        {monthlyStatusData.length === 0 ? (
          <p style={{ color: "#94A3B8", fontSize: "0.875rem", textAlign: "center", padding: "24px 0" }}>No timesheet data yet.</p>
        ) : (
          <div style={{ overflowX: "auto", paddingBottom: 8 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end", height: 220, minWidth: monthlyStatusData.length * 52 }}>
              {monthlyStatusData.map((d, i) => {
                const total = d.approvedHrs + d.pendingHrs + d.rejectedHrs;
                const barH = (total / maxMonthHrs) * 150;
                const approvedH = total > 0 ? (d.approvedHrs / total) * barH : 0;
                const pendingH = total > 0 ? (d.pendingHrs / total) * barH : 0;
                const rejectedH = total > 0 ? (d.rejectedHrs / total) * barH : 0;
                return (
                  <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 44 }} title={`${d.label}\nApproved: ${d.approvedHrs}h\nPending: ${d.pendingHrs}h\nRejected: ${d.rejectedHrs}h`}>
                    <span style={{ fontSize: "0.65rem", fontWeight: 600, color: "#0F172A" }}>{total}h</span>
                    <div style={{ width: 32, display: "flex", flexDirection: "column-reverse", borderRadius: "5px 5px 0 0", overflow: "hidden", minHeight: 4 }}>
                      <div style={{ height: approvedH, background: "#10B981", transition: "height 0.3s" }} />
                      <div style={{ height: pendingH, background: "#F59E0B", transition: "height 0.3s" }} />
                      <div style={{ height: rejectedH, background: "#EF4444", transition: "height 0.3s" }} />
                    </div>
                    <span style={{ fontSize: "0.62rem", fontWeight: 600, color: "#64748B", whiteSpace: "nowrap" }}>{d.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Approval rate summary */}
      <div style={{ background: "#fff", border: "1px solid #E2EEFF", borderRadius: 14, padding: "20px 24px", boxShadow: "0 1px 4px rgba(37,99,235,0.06)" }}>
        <h4 style={{ fontSize: "0.9375rem", fontWeight: 700, color: "#0F172A", marginBottom: 14 }}>Your Approval Rate</h4>
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <div style={{ width: 80, height: 80, position: "relative" }}>
            <svg width="80" height="80" viewBox="0 0 80 80">
              <circle cx="40" cy="40" r="32" fill="none" stroke="#F1F5F9" strokeWidth="6" />
              <circle cx="40" cy="40" r="32" fill="none" stroke="#10B981" strokeWidth="6"
                strokeDasharray={`${(tsValues.length > 0 ? (approved.length / tsValues.length) : 0) * 2 * Math.PI * 32} ${2 * Math.PI * 32}`}
                strokeLinecap="round" transform="rotate(-90 40 40)" />
            </svg>
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.875rem", fontWeight: 800, color: "#0F172A" }}>
              {tsValues.length > 0 ? Math.round((approved.length / tsValues.length) * 100) : 0}%
            </div>
          </div>
          <div>
            <p style={{ fontSize: "0.84rem", fontWeight: 600, color: "#1E293B", marginBottom: 4 }}>{approved.length} of {tsValues.length} timesheets approved</p>
            <p style={{ fontSize: "0.78rem", color: "#64748B" }}>{approvedHours}h approved out of {totalHours}h total logged</p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Calendar view ── */
const CalendarView = ({ submittedTimesheets, approvedLeaveDates, holidayDates = new Set() }) => {
  const today = dayjs();
  const [viewMonth, setViewMonth] = useState(today.startOf("month"));

  const daysInMonth = viewMonth.daysInMonth();
  const firstDay = viewMonth.startOf("month").day(); // 0=Sun
  const cells = Array.from({ length: firstDay + daysInMonth }, (_, i) =>
    i < firstDay ? null : viewMonth.date(i - firstDay + 1)
  );

  const getStatus = (d) => {
    if (!d) return null;
    const dateStr = d.format("YYYY-MM-DD");
    if (holidayDates.has(dateStr)) return "holiday";
    if (approvedLeaveDates.has(dateStr)) return "leave";
    const weekSun = d.day() === 0 ? d : d.subtract(d.day(), "day");
    const key = weekSun.format("YYYY-MM-DD");
    const ts = submittedTimesheets[key];
    if (!ts) return null;
    return ts.status?.toLowerCase().replace(/\s+/g, "-");
  };

  const STATUS_STYLE = {
    "approved": { bg: "#ECFDF5", color: "#059669" },
    "pending": { bg: "#FFFBEB", color: "#D97706" },
    "submitted": { bg: "#FFFBEB", color: "#D97706" },
    "rejected": { bg: "#FEF2F2", color: "#DC2626" },
    "not-submitted": { bg: "#F8FAFC", color: "#94A3B8" },
    "leave": { bg: "#EFF6FF", color: "#2563EB" },
    "holiday": { bg: "#FEF3C7", color: "#B45309" },
  };

  return (
    <div style={{ background: "#fff", border: "1px solid #E2EEFF", borderRadius: 14, padding: "20px 24px", boxShadow: "0 1px 4px rgba(37,99,235,0.06)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <h4 style={{ fontSize: "0.9375rem", fontWeight: 700, color: "#0F172A" }}>{viewMonth.format("MMMM YYYY")}</h4>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="emp-carousel-arrow" onClick={() => setViewMonth(v => v.subtract(1, "month"))}>‹</button>
          <button className="emp-carousel-arrow" onClick={() => setViewMonth(v => v.add(1, "month"))}>›</button>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4 }}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
          <div key={d} style={{ textAlign: "center", fontSize: "0.68rem", fontWeight: 700, color: "#94A3B8", padding: "4px 0" }}>{d}</div>
        ))}
        {cells.map((d, i) => {
          const st = d ? getStatus(d) : null;
          const style = st ? STATUS_STYLE[st] || {} : {};
          const isToday = d && d.isSame(today, "day");
          return (
            <div key={i} style={{
              textAlign: "center", padding: "6px 2px", borderRadius: 8,
              fontSize: "0.75rem", fontWeight: isToday ? 700 : 500,
              color: isToday ? "#fff" : (style.color || "#475569"),
              background: isToday ? "#2563EB" : (style.bg || "transparent"),
              border: isToday ? "none" : (st ? `1px solid ${style.color}30` : "1px solid transparent"),
            }}>
              {d ? d.date() : ""}
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 16, fontSize: "0.72rem" }}>
        {[["#059669", "#ECFDF5", "Approved"], ["#D97706", "#FFFBEB", "Pending"], ["#DC2626", "#FEF2F2", "Rejected"], ["#2563EB", "#EFF6FF", "Leave"], ["#B45309", "#FEF3C7", "Holiday"]].map(([c, bg, l]) => (
          <span key={l} style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 8px", background: bg, borderRadius: 999, color: c, fontWeight: 600 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: c }} />
            {l}
          </span>
        ))}
      </div>
    </div>
  );
};

/* ── Feedback view ── */
const FeedbackView = ({ username, name }) => {
  const [wentWell, setWentWell] = useState("");
  const [wentWrong, setWentWrong] = useState("");
  const [improvements, setImprovements] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [feedbackList, setFeedbackList] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [editWentWell, setEditWentWell] = useState("");
  const [editWentWrong, setEditWentWrong] = useState("");
  const [editImprovements, setEditImprovements] = useState("");
  const [editIsAnonymous, setEditIsAnonymous] = useState(false);

  const currentMonthStr = dayjs().format("MMMM YYYY"); // e.g. "August 2026"
  const storageKey = `workforcemgt_feedback_${username}`;

  // Load history from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        setFeedbackList(JSON.parse(stored));
      }
    } catch (e) {
      console.error("Error loading feedback history", e);
    }
  }, [storageKey]);

  // Check if current month is already submitted
  const currentMonthFeedback = feedbackList.find(f => f.month === currentMonthStr);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!wentWell.trim() || !wentWrong.trim() || !improvements.trim()) return;

    setLoading(true);
    const newFeedback = {
      id: uuidv4(),
      month: currentMonthStr,
      wentWell: wentWell.trim(),
      wentWrong: wentWrong.trim(),
      improvements: improvements.trim(),
      isAnonymous: isAnonymous,
      dateSubmitted: dayjs().format("MMM D, YYYY h:mm A")
    };

    try {
      await fetch("/timesheets/feedback/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHdr()
        },
        body: JSON.stringify({
          username,
          name,
          month: currentMonthStr,
          wentWell: wentWell.trim(),
          wentWrong: wentWrong.trim(),
          improvements: improvements.trim(),
          isAnonymous: isAnonymous
        })
      });
    } catch (err) {
      console.error("Error submitting feedback notification to backend", err);
    }

    const updatedList = [newFeedback, ...feedbackList];
    setFeedbackList(updatedList);
    localStorage.setItem(storageKey, JSON.stringify(updatedList));

    setWentWell("");
    setWentWrong("");
    setImprovements("");
    setIsAnonymous(false);
    setLoading(false);
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditWentWell(item.wentWell || "");
    setEditWentWrong(item.wentWrong || "");
    setEditImprovements(item.improvements || "");
    setEditIsAnonymous(item.isAnonymous || false);
  };

  const handleSaveEdit = (id) => {
    if (!editWentWell.trim() || !editWentWrong.trim() || !editImprovements.trim()) return;
    const updatedList = feedbackList.map(f => {
      if (f.id === id) {
        return {
          ...f,
          wentWell: editWentWell.trim(),
          wentWrong: editWentWrong.trim(),
          improvements: editImprovements.trim(),
          isAnonymous: editIsAnonymous,
          modified: true,
          dateModified: dayjs().format("MMM D, YYYY h:mm A")
        };
      }
      return f;
    });
    setFeedbackList(updatedList);
    localStorage.setItem(storageKey, JSON.stringify(updatedList));
    setEditingId(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Retrospective Form / Current Submission Card */}
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 16, padding: "32px", boxShadow: "0 4px 20px rgba(0,0,0,0.02)", display: "flex", flexDirection: "column", gap: 24, position: "relative" }}>
        <div style={{ position: "absolute", right: 32, top: 20, pointerEvents: "none", zIndex: 10 }}>
          {/* Inline SVG illustration */}
          <svg width="160" height="135" viewBox="0 0 160 135" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Background soft blob */}
            <path d="M-5 65C-5 35 15 15 45 15C75 15 95 30 95 60C95 90 70 110 40 110C10 110 -5 95 -5 65Z" fill="#EFF6FF" opacity="0.9" />
            {/* Floating sparkles */}
            <path d="M5 25 L6.5 28.5 L10 30 L6.5 31.5 L5 35 L3.5 31.5 L0 30 L3.5 28.5 Z" fill="#93C5FD" opacity="0.6" />
            <path d="M90 40 L91.5 43.5 L95 45 L91.5 46.5 L90 50 L88.5 46.5 L85 45 L88.5 43.5 Z" fill="#93C5FD" opacity="0.5" />
            <path d="M95 85 L96.5 88.5 L100 90 L96.5 91.5 L95 95 L93.5 91.5 L90 90 L93.5 88.5 Z" fill="#93C5FD" opacity="0.4" />

            {/* Clipboard Document */}
            <rect x="12" y="32" width="66" height="78" rx="8" fill="#FFFFFF" stroke="#D1E2FF" strokeWidth="2.5" />

            {/* Clipboard top clip */}
            <rect x="32" y="27" width="26" height="8" rx="3" fill="#BACFFD" />

            {/* Lines on clipboard */}
            <rect x="22" y="48" width="46" height="3" rx="1.5" fill="#E2EEFF" />
            <rect x="22" y="58" width="46" height="3" rx="1.5" fill="#E2EEFF" />
            <rect x="22" y="68" width="28" height="3" rx="1.5" fill="#E2EEFF" />

            {/* Blue Pencil pointing down-left */}
            <g transform="translate(88, 72) rotate(-40)">
              {/* Eraser */}
              <rect x="-6" y="-35" width="12" height="5" fill="#93C5FD" rx="1" />
              {/* Body */}
              <rect x="-6" y="-30" width="12" height="25" fill="#2563EB" />
              {/* Tip */}
              <polygon points="-6,-5 0,5 6,-5" fill="#E2EEFF" />
              {/* Lead */}
              <polygon points="-2.5,-1 0,5 2.5,-1" fill="#2563EB" />
            </g>

            {/* Speech Bubble */}
            <path d="M40 22C40 11.5 49 3 60 3H88C99 3 108 11.5 108 22C108 32.5 99 41 88 41H78L67 49V41H60C49 41 40 32.5 40 22Z" fill="#2563EB" />
            {/* Speech bubble lines */}
            <rect x="54" y="15" width="40" height="3" rx="1.5" fill="#FFFFFF" />
            <rect x="54" y="24" width="28" height="3" rx="1.5" fill="#FFFFFF" />
          </svg>
        </div>
        {currentMonthFeedback ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#ECFDF5", border: "1px solid #A7F3D0", padding: "8px 12px", borderRadius: 8, marginRight: 180 }}>
              <Icon.CheckCircle size={16} color="#10B981" style={{ flexShrink: 0 }} />
              <div style={{ fontSize: "0.8rem", color: "#065F46", fontWeight: 600 }}>
                You have already submitted your company feedback for {currentMonthStr}. Thank you!
              </div>
            </div>

            <div style={{ borderTop: "1px solid #EFF6FF", paddingTop: 8, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ flex: 1, paddingRight: 180 }}>
                  <h4 style={{ fontSize: "0.95rem", fontWeight: 700, color: "#0F172A", margin: 0 }}>Your {currentMonthStr} Feedback</h4>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "2px 0 6px" }}>
                    <span style={{ fontSize: "0.72rem", color: "#94A3B8" }}>
                      {currentMonthFeedback.dateModified ? `Modified: ${currentMonthFeedback.dateModified}` : `Submitted: ${currentMonthFeedback.dateSubmitted}`}
                    </span>
                    {currentMonthFeedback.modified && (
                      <span style={{ fontSize: "0.65rem", fontWeight: 600, background: "#F1F5F9", color: "#475569", padding: "1px 5px", borderRadius: 4 }}>
                        ✍️ Modified
                      </span>
                    )}
                  </div>
                  {!currentMonthFeedback.modified && editingId !== currentMonthFeedback.id && (
                    <button
                      onClick={() => startEdit(currentMonthFeedback)}
                      style={{ background: "none", color: "#2563EB", cursor: "pointer", fontSize: "0.72rem", fontWeight: 600, padding: "4px 8px", border: "1px solid #E2D8F0", borderRadius: 6, display: "inline-flex", alignItems: "center", gap: 4 }}
                      title="Modify Feedback"
                    >
                      ✏️ Modify
                    </button>
                  )}
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {[
                  {
                    title: "Highlights: What went well at the company or in your team this month?",
                    value: currentMonthFeedback.wentWell,
                    color: "#10B981",
                    icon: (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    )
                  },
                  {
                    title: "Challenges: What went wrong, fell short, or was not up to standard this month?",
                    value: currentMonthFeedback.wentWrong,
                    color: "#EF4444",
                    icon: (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                    )
                  },
                  {
                    title: "Actionable Ideas: What can we do more of to improve your experience next month?",
                    value: currentMonthFeedback.improvements,
                    color: "#F59E0B",
                    icon: (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A5 5 0 0 0 8 8c0 1 .3 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
                        <line x1="9" y1="18" x2="15" y2="18" />
                        <line x1="10" y1="22" x2="14" y2="22" />
                      </svg>
                    )
                  }
                ].map(item => (
                  <div key={item.title} style={{ display: "flex", gap: 18, border: "1px solid #E2E8F0", borderRadius: 12, padding: "20px 24px", background: "#fff", alignItems: "flex-start" }}>
                    <div style={{ width: 42, height: 42, borderRadius: "50%", background: item.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {item.icon}
                    </div>
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
                      <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "#0F172A", lineHeight: 1.3 }}>{item.title}</span>
                      <p style={{ fontSize: "0.8125rem", fontWeight: 400, color: "#475569", margin: 0, whiteSpace: "pre-line", lineHeight: 1.4 }}>{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {/* Header with Title, Subtitle */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 20 }}>
              <div style={{ flex: 1, paddingRight: 180 }}>
                <h4 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#0F172A", margin: "0 0 8px" }}>
                  Monthly Company Feedback ({currentMonthStr})
                </h4>
                <p style={{ fontSize: "0.8rem", color: "#475569", margin: 0, lineHeight: 1.5 }}>
                  Share your monthly feedback about the company. Help us understand what went well, what fell short, and how we can support you better.
                </p>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Question 1 */}
              <div style={{
                display: "flex",
                gap: 14,
                border: "1px solid #E2E8F0",
                borderRadius: 12,
                padding: "16px 20px",
                background: "#fff",
                alignItems: "flex-start"
              }}>
                <div style={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  background: "#10B981",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                  <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#0F172A", lineHeight: 1.3 }}>
                    Highlights: What went well at the company or in your team this month?
                  </label>
                  <textarea
                    rows="2"
                    value={wentWell}
                    onChange={(e) => setWentWell(e.target.value)}
                    placeholder="E.g., positive team collaborations, successful milestones, supportive management, or positive workplace moments..."
                    required
                    style={{
                      width: "100%",
                      height: "54px",
                      padding: "8px 12px",
                      background: "#fff",
                      border: "1px solid #CBD5E1",
                      borderRadius: 6,
                      fontSize: "0.8125rem",
                      fontWeight: 400,
                      color: "#0F172A",
                      outline: "none",
                      resize: "vertical",
                      fontFamily: "inherit",
                      lineHeight: 1.4,
                      boxSizing: "border-box",
                      transition: "border-color 0.2s, box-shadow 0.2s"
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#2563EB";
                      e.target.style.boxShadow = "0 0 0 3px rgba(37, 99, 235, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#CBD5E1";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>
              </div>

              {/* Question 2 */}
              <div style={{
                display: "flex",
                gap: 14,
                border: "1px solid #E2E8F0",
                borderRadius: 12,
                padding: "16px 20px",
                background: "#fff",
                alignItems: "flex-start"
              }}>
                <div style={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  background: "#EF4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                  <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#0F172A", lineHeight: 1.3 }}>
                    Challenges: What went wrong, fell short, or was not up to standard this month?
                  </label>
                  <textarea
                    rows="2"
                    value={wentWrong}
                    onChange={(e) => setWentWrong(e.target.value)}
                    placeholder="E.g., tool or process blockers, communication gaps, or aspects that did not meet expectations..."
                    required
                    style={{
                      width: "100%",
                      height: "54px",
                      padding: "8px 12px",
                      background: "#fff",
                      border: "1px solid #CBD5E1",
                      borderRadius: 6,
                      fontSize: "0.8125rem",
                      fontWeight: 400,
                      color: "#0F172A",
                      outline: "none",
                      resize: "vertical",
                      fontFamily: "inherit",
                      lineHeight: 1.4,
                      boxSizing: "border-box",
                      transition: "border-color 0.2s, box-shadow 0.2s"
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#2563EB";
                      e.target.style.boxShadow = "0 0 0 3px rgba(37, 99, 235, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#CBD5E1";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>
              </div>

              {/* Question 3 */}
              <div style={{
                display: "flex",
                gap: 14,
                border: "1px solid #E2E8F0",
                borderRadius: 12,
                padding: "16px 20px",
                background: "#fff",
                alignItems: "flex-start"
              }}>
                <div style={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  background: "#F59E0B",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A5 5 0 0 0 8 8c0 1 .3 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
                    <line x1="9" y1="18" x2="15" y2="18" />
                    <line x1="10" y1="22" x2="14" y2="22" />
                  </svg>
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                  <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#0F172A", lineHeight: 1.3 }}>
                    Actionable Ideas: What can we do more of to improve your experience next month?
                  </label>
                  <textarea
                    rows="2"
                    value={improvements}
                    onChange={(e) => setImprovements(e.target.value)}
                    placeholder="E.g., specific changes, resources, or support you would like to see implemented..."
                    required
                    style={{
                      width: "100%",
                      height: "54px",
                      padding: "8px 12px",
                      background: "#fff",
                      border: "1px solid #CBD5E1",
                      borderRadius: 6,
                      fontSize: "0.8125rem",
                      fontWeight: 400,
                      color: "#0F172A",
                      outline: "none",
                      resize: "vertical",
                      fontFamily: "inherit",
                      lineHeight: 1.4,
                      boxSizing: "border-box",
                      transition: "border-color 0.2s, box-shadow 0.2s"
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#2563EB";
                      e.target.style.boxShadow = "0 0 0 3px rgba(37, 99, 235, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#CBD5E1";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Anonymity Checkbox */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "8px 0" }}>
              <input
                type="checkbox"
                id="submit-anonymously"
                checked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 4,
                  border: "1.5px solid #CBD5E1",
                  cursor: "pointer",
                  accentColor: "#2563EB"
                }}
              />
              <label
                htmlFor="submit-anonymously"
                style={{ fontSize: "0.875rem", fontWeight: 500, color: "#475569", cursor: "pointer", userSelect: "none" }}
              >
                Submit feedback anonymously (your name will not be shown to the employer)
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || !wentWell.trim() || !wentWrong.trim() || !improvements.trim()}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "12px 24px",
                background: loading || !wentWell.trim() || !wentWrong.trim() || !improvements.trim() ? "#94A3B8" : "#2563EB",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: loading || !wentWell.trim() || !wentWrong.trim() || !improvements.trim() ? "not-allowed" : "pointer",
                transition: "background 0.2s, transform 0.1s",
                alignSelf: "flex-start"
              }}
              onMouseEnter={(e) => {
                if (!loading && wentWell.trim() && wentWrong.trim() && improvements.trim()) {
                  e.target.style.background = "#1D4ED8";
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && wentWell.trim() && wentWrong.trim() && improvements.trim()) {
                  e.target.style.background = "#2563EB";
                }
              }}
            >
              {loading ? "Submitting..." : "Submit Monthly Feedback"}
              {!loading && <Icon.ArrowRight size={16} color="#fff" />}
            </button>
          </form>
        )}
      </div>

      {/* Feedback History Card */}
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 16, padding: "32px", boxShadow: "0 4px 20px rgba(0,0,0,0.02)", display: "flex", flexDirection: "column", gap: 24 }}>
        <h4 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#0F172A", margin: 0 }}>Feedback History</h4>

        {feedbackList.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "40px 10px", color: "#94A3B8", fontSize: "0.875rem", textAlign: "center", border: "1px dashed #E2E8F0", borderRadius: 12 }}>
            <Icon.FileText size={32} color="#CBD5E1" />
            <p style={{ margin: 0, color: "#64748B" }}>No feedback submitted yet. Your monthly retrospectives will be saved here.</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {feedbackList.map((item) => {
              const isEditing = editingId === item.id;
              return (
                <div key={item.id} style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: "20px", background: "#fff", display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #F1F5F9", paddingBottom: 12, margin: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: "0.95rem", fontWeight: 700, color: "#0F172A" }}>{item.month}</span>
                      {item.isAnonymous && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.7rem", fontWeight: 600, background: "#EFF6FF", color: "#2563EB", padding: "3px 8px", borderRadius: 999, border: "1px solid #DBEAFE" }}>
                          <Icon.Lock size={10} color="#2563EB" /> Anonymous
                        </span>
                      )}
                      {item.modified && (
                        <span style={{ display: "inline-flex", alignItems: "center", fontSize: "0.7rem", fontWeight: 600, background: "#F1F5F9", color: "#475569", padding: "3px 8px", borderRadius: 999, border: "1px solid #E2E8F0" }}>
                          Modified
                        </span>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: "0.75rem", color: "#94A3B8" }}>
                        {item.dateModified ? `Modified: ${item.dateModified}` : `Submitted: ${item.dateSubmitted}`}
                      </span>
                      {!isEditing && !item.modified && (
                        <button
                          onClick={() => startEdit(item)}
                          style={{ background: "none", border: "1px solid #E2E8F0", color: "#2563EB", cursor: "pointer", fontSize: "0.75rem", fontWeight: 600, padding: "5px 10px", borderRadius: 6, display: "inline-flex", alignItems: "center", gap: 4, transition: "all 0.2s" }}
                          onMouseEnter={(e) => { e.target.style.borderColor = "#2563EB"; e.target.style.background = "#EFF6FF"; }}
                          onMouseLeave={(e) => { e.target.style.borderColor = "#E2E8F0"; e.target.style.background = "none"; }}
                          title="Modify Feedback"
                        >
                          <Icon.Edit size={12} color="#2563EB" /> Modify
                        </button>
                      )}
                    </div>
                  </div>

                  {isEditing ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <label style={{ fontSize: "0.8rem", fontWeight: 700, color: "#10B981", textTransform: "uppercase", letterSpacing: "0.04em" }}>🟢 Highlights:</label>
                        <textarea
                          rows="2"
                          value={editWentWell}
                          onChange={(e) => setEditWentWell(e.target.value)}
                          style={{ padding: "10px 14px", border: "1.5px solid #CBD5E1", borderRadius: 8, fontSize: "0.875rem", color: "#0F172A", outline: "none", resize: "vertical", fontFamily: "inherit", lineHeight: 1.5, boxSizing: "border-box" }}
                        />
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <label style={{ fontSize: "0.8rem", fontWeight: 700, color: "#EF4444", textTransform: "uppercase", letterSpacing: "0.04em" }}>🔴 Challenges:</label>
                        <textarea
                          rows="2"
                          value={editWentWrong}
                          onChange={(e) => setEditWentWrong(e.target.value)}
                          style={{ padding: "10px 14px", border: "1.5px solid #CBD5E1", borderRadius: 8, fontSize: "0.875rem", color: "#0F172A", outline: "none", resize: "vertical", fontFamily: "inherit", lineHeight: 1.5, boxSizing: "border-box" }}
                        />
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <label style={{ fontSize: "0.8rem", fontWeight: 700, color: "#F59E0B", textTransform: "uppercase", letterSpacing: "0.04em" }}>💡 Actionable Ideas:</label>
                        <textarea
                          rows="2"
                          value={editImprovements}
                          onChange={(e) => setEditImprovements(e.target.value)}
                          style={{ padding: "10px 14px", border: "1.5px solid #CBD5E1", borderRadius: 8, fontSize: "0.875rem", color: "#0F172A", outline: "none", resize: "vertical", fontFamily: "inherit", lineHeight: 1.5, boxSizing: "border-box" }}
                        />
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "4px 0" }}>
                        <input
                          type="checkbox"
                          id={`edit-anonymously-${item.id}`}
                          checked={editIsAnonymous}
                          onChange={(e) => setEditIsAnonymous(e.target.checked)}
                          style={{
                            width: 14,
                            height: 14,
                            borderRadius: 4,
                            border: "1.5px solid #CBD5E1",
                            cursor: "pointer",
                            accentColor: "#2563EB"
                          }}
                        />
                        <label
                          htmlFor={`edit-anonymously-${item.id}`}
                          style={{ fontSize: "0.8rem", fontWeight: 600, color: "#475569", cursor: "pointer", userSelect: "none" }}
                        >
                          Submit feedback anonymously
                        </label>
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                        <button
                          onClick={() => handleSaveEdit(item.id)}
                          disabled={!editWentWell.trim() || !editWentWrong.trim() || !editImprovements.trim()}
                          style={{ padding: "8px 18px", background: "#2563EB", color: "#fff", border: "none", borderRadius: 8, fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", transition: "background 0.2s" }}
                          onMouseEnter={(e) => { e.target.style.background = "#1D4ED8"; }}
                          onMouseLeave={(e) => { e.target.style.background = "#2563EB"; }}
                        >
                          Save Changes
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          style={{ padding: "8px 18px", background: "#F1F5F9", color: "#475569", border: "none", borderRadius: 8, fontSize: "0.8rem", fontWeight: 600, cursor: "pointer" }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {/* Highlights */}
                      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                        <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#10B981", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                          </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748B" }}>Highlights & Successes</span>
                          <p style={{ fontSize: "0.875rem", color: "#334155", margin: "4px 0 0", lineHeight: 1.5, background: "#F8FAFC", padding: "10px 14px", borderRadius: 8, border: "1px solid #F1F5F9" }}>{item.wentWell}</p>
                        </div>
                      </div>

                      {/* Challenges */}
                      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                        <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#EF4444", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                          </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748B" }}>Challenges & Gaps</span>
                          <p style={{ fontSize: "0.875rem", color: "#334155", margin: "4px 0 0", lineHeight: 1.5, background: "#F8FAFC", padding: "10px 14px", borderRadius: 8, border: "1px solid #F1F5F9" }}>{item.wentWrong}</p>
                        </div>
                      </div>

                      {/* Actionable Ideas */}
                      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                        <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#F59E0B", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A5 5 0 0 0 8 8c0 1 .3 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
                            <line x1="9" y1="18" x2="15" y2="18" />
                            <line x1="10" y1="22" x2="14" y2="22" />
                          </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748B" }}>Actionable Ideas & Suggestions</span>
                          <p style={{ fontSize: "0.875rem", color: "#334155", margin: "4px 0 0", lineHeight: 1.5, background: "#F8FAFC", padding: "10px 14px", borderRadius: 8, border: "1px solid #F1F5F9" }}>{item.improvements}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Settings view ── */
const SettingsView = ({ username, name, startDate: propStartDate, showToast }) => {
  const [selectedClient, setSelectedClient] = useState("Internal");
  const [profileData, setProfileData] = useState(null);
  const [profileStartDate, setProfileStartDate] = useState(propStartDate);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [savingDob, setSavingDob] = useState(false);
  const [hasExistingDob, setHasExistingDob] = useState(false);

  useEffect(() => {
    // Fetch current user's profile
    (async () => {
      try {
        const res = await fetch(`/timesheets/users/${username}/profile`, { headers: { Authorization: `Bearer ${sessionStorage.getItem("jwtToken")}` } });
        if (res.ok) {
          const data = await res.json();
          setSelectedClient(data.clientName || "Internal");
          if (data.startDate) setProfileStartDate(data.startDate);
          if (data.dateOfBirth) {
            setDateOfBirth(data.dateOfBirth);
            setHasExistingDob(true);
          }
          setProfileData(data);
        }
      } catch (e) { console.error(e); }
    })();
  }, [username]);

  const handleSaveDob = async () => {
    if (!dateOfBirth) return;
    setSavingDob(true);
    try {
      const res = await fetch(`/timesheets/users/${username}/dateOfBirth`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("jwtToken")}`
        },
        body: JSON.stringify({ dateOfBirth })
      });
      if (res.ok) {
        showToast("Date of Birth updated successfully.", "success");
        setHasExistingDob(true);
      } else {
        showToast("Failed to update Date of Birth.", "error");
      }
    } catch (e) {
      console.error(e);
      showToast("Failed to update Date of Birth.", "error");
    } finally {
      setSavingDob(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ background: "#fff", border: "1px solid #E2EEFF", borderRadius: 14, padding: "24px", boxShadow: "0 1px 4px rgba(37,99,235,0.06)" }}>
        <h4 style={{ fontSize: "0.9375rem", fontWeight: 700, color: "#0F172A", marginBottom: 20 }}>Profile Information</h4>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
          {(() => {
            const tz = (profileData?.timezone || "America/New_York").toLowerCase();
            const isAmericaTz = tz.startsWith("america/") || tz.includes("york") || tz.includes("est") || tz.includes("edt") || tz.includes("cst") || tz.includes("pst") || tz.includes("us") || tz.includes("america");
            const wp = profileData?.workPermit || "Citizen";
            const showExpiry = isAmericaTz && wp && wp.toLowerCase() !== "citizen";

            const fields = [
              { label: "Full Name", value: profileData?.name || name || "—" },
              { label: "Username", value: username || "—" },
              { label: "Email", value: profileData?.email || "—" },
              { label: "Start Date", value: profileStartDate ? dayjs(profileStartDate).format("MMM D, YYYY") : "—" },
              { label: "Role", value: "Employee" },
              { label: "Client", value: selectedClient || "—" },
              { label: "Timezone", value: profileData?.timezone || "—" },
              ...(isAmericaTz ? [
                { label: "Work Permit", value: profileData?.workPermit || "Citizen" },
                ...(showExpiry ? [
                  { label: "Expiry Date", value: profileData?.workPermitExpiry ? dayjs(profileData.workPermitExpiry).format("MMM D, YYYY") : "—" }
                ] : [])
              ] : [])
            ];

            return fields.map(f => (
              <div key={f.label} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em" }}>{f.label}</label>
                <div style={{ padding: "10px 14px", background: "#F8FAFC", border: "1.5px solid #E2E8F0", borderRadius: 10, fontSize: "0.875rem", fontWeight: 500, color: "#0F172A" }}>{f.value}</div>
              </div>
            ));
          })()}
        </div>

        <div style={{ borderTop: "1px solid #EFF6FF", paddingTop: 20, display: "flex", flexDirection: "column", gap: 12 }}>
          <h5 style={{ fontSize: "0.85rem", fontWeight: 700, color: "#0F172A", margin: 0 }}>Personal Details</h5>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 12, maxWidth: 400 }}>
            {hasExistingDob ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: 1 }}>
                <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em" }}>Date of Birth</label>
                <div style={{ padding: "10px 14px", background: "#F8FAFC", border: "1.5px solid #E2E8F0", borderRadius: 10, fontSize: "0.875rem", fontWeight: 500, color: "#0F172A" }}>
                  {dayjs(dateOfBirth).format("MMM D, YYYY")}
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: 1 }}>
                  <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em" }}>Date of Birth</label>
                  <input
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    style={{
                      padding: "10px 14px",
                      background: "#fff",
                      border: "1.5px solid #E2E8F0",
                      borderRadius: 10,
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#0F172A",
                      outline: "none",
                      fontFamily: "inherit"
                    }}
                  />
                </div>
                <button
                  onClick={handleSaveDob}
                  disabled={savingDob || !dateOfBirth}
                  style={{
                    padding: "11px 20px",
                    background: "#2563EB",
                    color: "#fff",
                    border: "none",
                    borderRadius: 10,
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    cursor: "pointer",
                    height: 42
                  }}
                >
                  {savingDob ? "Saving..." : "Save DOB"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <div style={{ background: "#F8FBFF", border: "1px solid #E2EEFF", borderRadius: 10, padding: "14px 18px" }}>
        <p style={{ fontSize: "0.8rem", color: "#64748B", margin: 0 }}>ℹ️ To update other profile details, please contact your employer.</p>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
const EmployeeHome = () => {
  const { username, name } = useAuth();

  const [sundays, setSundays] = useState([]);
  const [submittedTimesheets, setSubmittedTimesheets] = useState({});
  const [startDate, setStartDate] = useState(null);
  const [clientNotesEnabled, setClientNotesEnabled] = useState(false);
  const [approvedLeaveDates, setApprovedLeaveDates] = useState(new Set());
  const [holidayDates, setHolidayDates] = useState(new Set());
  const [leaveBalance, setLeaveBalance] = useState(10);
  const [approvedLeaveDays, setApprovedLeaveDays] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeNav, setActiveNav] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get("tab") || params.get("nav");
      if (tab && NAV.some(n => n.key === tab.toLowerCase())) {
        return tab.toLowerCase();
      }
    } catch (e) {
      // ignore
    }
    return "dashboard";
  });

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get("tab") || params.get("nav");
      if (tab && NAV.some(n => n.key === tab.toLowerCase())) {
        setActiveNav(tab.toLowerCase());
      }
    } catch (e) {
      // ignore
    }
  }, []);
  // const [carouselIdx,   setCarouselIdx]   = useState(0);
  // const [openWeek,      setOpenWeek]      = useState(null);
  // const [currentPage,   setCurrentPage]   = useState(1);
  // const [activeTab]                       = useState("Recent");
  // const [searchQuery]                     = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState("");
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ leaveType: "", startDate: "", endDate: "", reason: "" });
  const [leaveSubmitting, setLeaveSubmitting] = useState(false);
  const [leaveSuccess, setLeaveSuccess] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [supportMsg, setSupportMsg] = useState("");
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [tasksVersion, setTasksVersion] = useState(0);
  const [toast, setToast] = useState(null); // { msg, type: "success"|"error"|"info" }
  // const bellRef = useRef(null);
  // const itemsPerPage = 8;

  const showToast = useCallback((msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchSubmittedTimesheets = useCallback(async () => {
    if (!sundays.length) return;
    const ws = dayjs(sundays[sundays.length - 1]).format("YYYY-MM-DD");
    const we = dayjs(sundays[0]).format("YYYY-MM-DD");
    try {
      const res = await fetch(`/timesheets/operations/byDates/${username}?weekStart=${ws}&weekEnd=${we}`, { headers: authHdr() });
      if (res.ok) {
        const data = await res.json();
        const map = {};
        (data.timesheetsResponseList || []).forEach(t => { map[t.weekStart] = t; });
        sundays.forEach(s => { if (!map[s]) map[s] = { weekStart: s, status: "Not Submitted" }; });
        setSubmittedTimesheets(map);
      }
    } catch (e) { console.error(e); }
  }, [sundays, username]);

  const generateSundays = useCallback((sd) => {
    const today = dayjs();
    // Show next week only from Sunday onwards; otherwise show current week as latest
    const last = today.day() === 0 ? today : today.day(0);
    const arr = [];
    for (let i = 0; i < 260; i++) {
      const w = last.subtract(i, "week");
      if (sd && w.add(6, "day").isBefore(dayjs(sd), "day")) continue;
      arr.push(w.format("YYYY-MM-DD"));
    }
    setSundays(arr);
  }, []);

  useEffect(() => { if (sundays.length) fetchSubmittedTimesheets(); }, [sundays, fetchSubmittedTimesheets]);

  const fetchLeaveDates = useCallback(async () => {
    if (!username) return;
    try {
      const res = await fetch(`/timesheets/leaves/user/${username}`, { headers: authHdr() });
      if (res.ok) {
        const leaves = await res.json();
        const dates = new Set();
        let approvedDaysCount = 0;
        const currentYear = dayjs().year();
        leaves.forEach(l => {
          if (l.status === "APPROVED") {
            const start = dayjs(l.startDate);
            if (start.year() === currentYear) {
              const end = dayjs(l.endDate);
              const diff = end.diff(start, "day") + 1;
              approvedDaysCount += diff;
            }
          }
          if (l.status === "PENDING" || l.status === "APPROVED") {
            let cur = dayjs(l.startDate), end = dayjs(l.endDate);
            while (!cur.isAfter(end, "day")) { dates.add(cur.format("YYYY-MM-DD")); cur = cur.add(1, "day"); }
          }
        });
        setApprovedLeaveDates(dates);
        setApprovedLeaveDays(approvedDaysCount);
      }
    } catch (e) { console.error(e); }
  }, [username]);

  // Fetch holidays applicable to this employee (resolved server-side by the
  // employee's region/timezone) and expand them into a Set of YYYY-MM-DD dates.
  const fetchHolidayDates = useCallback(async () => {
    if (!username) return;
    try {
      const res = await fetch(`/timesheets/holidays/user/${username}`, { headers: authHdr() });
      if (res.ok) {
        const holidays = await res.json();
        const dates = new Set();
        (holidays || []).forEach(h => { if (h.date) dates.add(h.date); });
        setHolidayDates(dates);
      }
    } catch (e) { console.error(e); }
  }, [username]);

  useEffect(() => {
    if (!username) return;
    (async () => {
      try {
        const res = await fetch(`/timesheets/users/${username}/profile`, { headers: authHdr() });
        if (res.ok) {
          const profile = await res.json();
          setStartDate(profile.startDate || null);
          setClientNotesEnabled(!!profile.clientNotesEnabled);
          generateSundays(profile.startDate || null);
          if (profile.leaveBalance !== undefined) {
            setLeaveBalance(profile.leaveBalance);
          }
        } else {
          generateSundays(null);
        }
      } catch { generateSundays(null); }
      await fetchLeaveDates();
      await fetchHolidayDates();
    })();
  }, [username, generateSundays, fetchLeaveDates, fetchHolidayDates]);

  const fetchNotifications = useCallback(async () => {
    if (!username) return;
    try {
      const res = await fetch(`/timesheets/notifications/unread/${username}`, { headers: authHdr() });
      if (res.ok) setNotifications(await res.json());
    } catch (e) { console.error(e); }
  }, [username]);

  useEffect(() => {
    fetchNotifications();
    const iv = setInterval(fetchNotifications, 30000);
    return () => clearInterval(iv);
  }, [fetchNotifications]);

  // Refresh holiday dates every 5 minutes so newly set holidays appear without a full reload
  useEffect(() => {
    if (!username) return;
    const iv = setInterval(fetchHolidayDates, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [username, fetchHolidayDates]);

  const markAllRead = async () => {
    try {
      await fetch(`/timesheets/notifications/read-all/${username}`, { method: "PATCH", headers: authHdr() });
      setNotifications([]); setShowNotifications(false);
    } catch (e) { console.error(e); }
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    if (startDate && dayjs(leaveForm.startDate).isBefore(dayjs(startDate), "day")) {
      showToast(`Leave cannot start before your joining date (${dayjs(startDate).format("MMM D, YYYY")}).`, "error"); return;
    }
    if (leaveForm.leaveType === "other" && !leaveForm.reason?.trim()) {
      showToast("Reason is required when selecting 'Other' leave type.", "error"); return;
    }
    setLeaveSubmitting(true);
    try {
      const res = await fetch("/timesheets/leaves/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHdr() },
        body: JSON.stringify({ username, ...leaveForm }),
      });
      if (!res.ok) throw new Error();
      await fetchLeaveDates();
      setLeaveSuccess(true);
      setTimeout(() => { setLeaveSuccess(false); setShowLeaveModal(false); setLeaveForm({ leaveType: "", startDate: "", endDate: "", reason: "" }); }, 2000);
    } catch { showToast("Failed to submit leave request. Please try again.", "error"); }
    finally { setLeaveSubmitting(false); }
  };

  /* derived */
  const tsValues = Object.values(submittedTimesheets);
  const totalWeeks = sundays.length;
  const submittedCount = tsValues.filter(t => t.status && t.status !== "Not Submitted").length;
  const pendingCount = tsValues.filter(t => t.status === "Pending" || t.status === "Submitted").length;
  const submittedPct = totalWeeks > 0 ? Math.round((submittedCount / totalWeeks) * 100) : 0;
  const pendingPct = submittedCount > 0 ? Math.round((pendingCount / submittedCount) * 100) : 0;
  const remainingLeaveDays = Math.max(0, leaveBalance - approvedLeaveDays);
  const totalLeaveQuota = Math.max(Number(leaveBalance) || 0, 1);
  const leavePct = Math.min(100, Math.max(0, Math.round((remainingLeaveDays / totalLeaveQuota) * 100)));
  const thisWeekKey = dayjs().day() === 0 ? dayjs().format("YYYY-MM-DD") : dayjs().day(0).format("YYYY-MM-DD");
  const thisWeekHrs = Number(submittedTimesheets[thisWeekKey]?.totalHours || 0);
  const weekPct = Math.min(Math.round((thisWeekHrs / 40) * 100), 100);
  const daysToFriday = (() => { const today = dayjs(); const fri = today.day() <= 5 ? today.day(5) : today.add(1, "week").day(5); return fri.diff(today, "day"); })();
  const nextFriday = dayjs().day() <= 5 ? dayjs().day(5).format("dddd, MMM D, YYYY") : dayjs().add(1, "week").day(5).format("dddd, MMM D, YYYY");

  /* dashboard table */
  // const tabFilter    = TAB_STATUS[activeTab];
  // const filteredWeeks = sundays.filter(w => {
  //   const status = submittedTimesheets[w]?.status||"Not Submitted";
  //   if (tabFilter&&!tabFilter.includes(status)) return false;
  //   if (searchQuery) { const fmt=dayjs(w).format("MMM D, YYYY").toLowerCase(); return w.includes(searchQuery)||fmt.includes(searchQuery.toLowerCase()); }
  //   return true;
  // });
  // const totalPages = Math.ceil(filteredWeeks.length/itemsPerPage);
  // const pagedWeeks = filteredWeeks.slice((currentPage-1)*itemsPerPage, currentPage*itemsPerPage);

  // useEffect(()=>{ const iv=setInterval(()=>setCarouselIdx(i=>(i+1)%FEATURES.length),5000); return ()=>clearInterval(iv); },[]);

  const recentActivity = [
    ...notifications.slice(0, 2).map(n => ({
      icon: n.type?.includes("APPROVED") ? "green" : "blue",
      iconName: n.type?.includes("APPROVED") ? "CheckCircle" : "FileText",
      title: n.message || "Notification",
      time: n.createdAt ? new Date(n.createdAt).toLocaleString() : "",
    })),
    ...tsValues.filter(t => t.status === "Approved").slice(0, 1).map(t => ({
      icon: "green", iconName: "CheckCircle", title: "Timesheet approved", time: t.weekStart || "",
    })),
  ].slice(0, 3);

  const handleNavClick = (key) => {
    setActiveNav(key);
  };

  /* shared props for sub-views */
  const sharedTsProps = { sundays, submittedTimesheets, username, startDate, approvedLeaveDates, holidayDates, fetchSubmittedTimesheets, clientNotesEnabled };

  return (
    <div className="emp-dash">

      {/* ══ SIDEBAR ══ */}
      <nav className="emp-sidebar" aria-label="Employee navigation">
        <div className="emp-nav-section">
          {NAV.map(item => (
            <button key={item.key}
              className={`emp-nav-item ${activeNav === item.key ? "active" : ""}`}
              onClick={() => handleNavClick(item.key)}
              aria-current={activeNav === item.key ? "page" : undefined}>
              <span className="emp-nav-icon">
                {item.icon === "Home" && <Icon.Home size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
                {item.icon === "FileText" && <Icon.FileText size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
                {item.icon === "Target" && <Icon.Target size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
                {item.icon === "CheckCircle" && <Icon.CheckCircle size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
                {item.icon === "Umbrella" && <Icon.Umbrella size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
                {item.icon === "BarChart" && <Icon.BarChart size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
                {item.icon === "Calendar" && <Icon.Calendar size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
                {item.icon === "Badge" && <Icon.Badge size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
                {item.icon === "Bell" && <Icon.Bell size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
                {item.icon === "MessageSquare" && <Icon.MessageSquare size={15} color={activeNav === item.key ? "#2563EB" : "#94A3B8"} />}
              </span>
              {item.label}
              {item.key === "notifications" && notifications.length > 0 && (
                <span style={{ marginLeft: "auto", background: "#EF4444", color: "#fff", fontSize: "0.65rem", fontWeight: 700, padding: "1px 6px", borderRadius: 999, minWidth: 18, textAlign: "center" }}>
                  {notifications.length}
                </span>
              )}
            </button>
          ))}
        </div>
        {/* <div className="emp-sidebar-footer">
          <div className="emp-help-card">
            <div className="emp-help-icon"><Icon.AlertCircle size={16} color="#2563EB"/></div>
            <h4>Need Help?</h4>
            <p>We're here to assist you</p>
            <button className="emp-help-btn" onClick={() => setShowSupportModal(true)}>Contact Support</button>
          </div>
        </div> */}
      </nav>

      {/* ══ MAIN ══ */}
      <div className="emp-main">

        {/* Bell icon for notifications */}
        <div style={{ position: "fixed", top: 13, right: 130, zIndex: 1400 }}>
          <button onClick={() => setShowNotifications(!showNotifications)}
            style={{ position: "relative", width: 34, height: 34, borderRadius: 8, background: "transparent", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
            aria-label="Notifications">
            <Icon.Bell size={18} color="#64748B" />
            {notifications.length > 0 && (
              <span style={{ position: "absolute", top: 2, right: 2, width: 16, height: 16, borderRadius: 999, background: "#EF4444", color: "#fff", fontSize: "0.6rem", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{notifications.length}</span>
            )}
          </button>
        </div>

        {/* ─── NON-DASHBOARD VIEWS ─── */}
        {activeNav === "timesheets" && (
          <div className="emp-center" style={{ width: "100%" }}>
            <div style={{ padding: "20px 0 4px", fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "#2563EB" }}>Timesheets</div>
            <TimesheetsView {...sharedTsProps} />
          </div>
        )}

        {activeNav === "appraisals" && (
          <div className="emp-center" style={{ width: "100%" }}>
            <div style={{ padding: "20px 0 4px", fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "#2563EB" }}>My Tasks & Targets</div>
            <EmployeeTargetsAppraisalsView key={tasksVersion} username={username} name={name} />
          </div>
        )}

        {activeNav === "approvals" && (
          <div className="emp-center" style={{ width: "100%" }}>
            <div style={{ padding: "20px 0 4px", fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "#2563EB" }}>Approvals</div>
            <ApprovalsView {...sharedTsProps} />
          </div>
        )}

        {activeNav === "leave" && (
          <div className="emp-center" style={{ width: "100%" }}>
            <div style={{ padding: "20px 0 4px", fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "#2563EB" }}>Leave Management</div>
            <LeaveView username={username} startDate={startDate} approvedLeaveDates={approvedLeaveDates} onApplyLeave={() => setShowLeaveModal(true)} />
          </div>
        )}

        {activeNav === "reports" && (
          <div className="emp-center" style={{ width: "100%" }}>
            <div style={{ padding: "20px 0 4px", fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "#2563EB" }}>Reports</div>
            <ReportsView sundays={sundays} submittedTimesheets={submittedTimesheets} />
          </div>
        )}

        {activeNav === "calendar" && (
          <div className="emp-center" style={{ width: "100%" }}>
            <div style={{ padding: "20px 0 4px", fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "#2563EB" }}>Calendar</div>
            <CalendarView submittedTimesheets={submittedTimesheets} approvedLeaveDates={approvedLeaveDates} holidayDates={holidayDates} />
          </div>
        )}

        {activeNav === "feedback" && (
          <div className="emp-center" style={{ width: "100%" }}>
            <div style={{ padding: "20px 0 4px", fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "#2563EB" }}>Feedback</div>
            <FeedbackView username={username} name={name} />
          </div>
        )}

        {activeNav === "settings" && (
          <div className="emp-center" style={{ width: "100%" }}>
            <div style={{ padding: "20px 0 4px", fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "#2563EB" }}>Settings</div>
            <SettingsView username={username} name={name} startDate={startDate} showToast={showToast} />
          </div>
        )}

        {activeNav === "notifications" && (
          <div className="emp-center" style={{ width: "100%" }}>
            <div style={{ padding: "20px 0 4px", fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "#2563EB" }}>Notifications</div>
            <div style={{ background: "#fff", border: "1px solid #E2EEFF", borderRadius: 14, padding: "20px", boxShadow: "0 1px 4px rgba(37,99,235,0.05)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <h3 style={{ fontSize: "0.9375rem", fontWeight: 700, color: "#0F172A" }}>All Notifications</h3>
                {notifications.length > 0 && <button style={{ fontSize: "0.75rem", fontWeight: 600, color: "#2563EB", background: "none", border: "none", cursor: "pointer" }} onClick={markAllRead}>Mark all read</button>}
              </div>
              {notifications.length === 0 ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "40px 20px", color: "#94A3B8", fontSize: "0.84rem" }}><Icon.CheckCircle size={28} color="#A7F3D0" /><p>You're all caught up!</p></div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {notifications.map(n => (
                    <div key={n.id} style={{ display: "flex", gap: 10, padding: "10px 14px", background: "#F8FBFF", borderRadius: 10, borderLeft: `3px solid ${n.type?.includes("APPROVED") ? "#10B981" : "#2563EB"}` }}>
                      <div style={{ flex: 1, fontSize: "0.8125rem", color: "#1E293B", fontWeight: 500 }}>{n.message}</div>
                      <div style={{ fontSize: "0.72rem", color: "#94A3B8", whiteSpace: "nowrap" }}>{n.createdAt ? dayjs(n.createdAt).format("MMM D") : ""}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── DASHBOARD (default) ─── */}
        {activeNav === "dashboard" && (
          <>
            <div className="emp-center">

              {/* HERO */}
              <motion.div className="emp-hero-card" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
                <div className="emp-hero-text">
                  <h1 className="emp-hero-title">{getGreeting()}, {name?.split(" ")[0] || "Employee"}!</h1>
                  <p className="emp-hero-sub">
                    Let's make today productive.
                    {pendingCount > 0 && <> You have <span className="emp-hero-pending-dot" /><strong>{pendingCount} timesheet{pendingCount === 1 ? "" : "s"}</strong> awaiting approval.</>}
                  </p>
                  {startDate && <p style={{ fontSize: "0.78rem", color: "#64748B", marginTop: 4 }}>Joined: {dayjs(startDate).format("MMM D, YYYY")}</p>}
                  <div className="emp-hero-actions">
                    <button className="emp-hero-btn-primary" onClick={() => {
                      const first = sundays.find(s => { const st = submittedTimesheets[s]?.status || "Not Submitted"; return st === "Not Submitted" || st === "Rejected"; });
                      if (first) { setSelectedWeek(first); setShowAddModal(true); } else showToast("All timesheets are either submitted or pending approval.", "info");
                    }}>
                      <Icon.FileText size={15} color="#fff" /> Submit Timesheet
                    </button>
                    <button className="emp-hero-btn-ghost" onClick={() => setShowLeaveModal(true)}>
                      Apply Leave
                    </button>
                    <button className="emp-hero-btn-ghost" onClick={() => setShowAddTaskModal(true)}>
                      <Icon.Target size={15} color="#2563EB" /> Add Task
                    </button>
                  </div>
                </div>
                <div className="emp-hero-illo" aria-hidden="true">
                  <div className="emp-hero-illo-inner"><Icon.Calendar size={52} color="#2563EB" strokeWidth={1.2} /></div>
                  <div className="emp-hero-illo-float"><Icon.Clock size={22} color="#F59E0B" strokeWidth={1.5} /></div>
                </div>
              </motion.div>

              {/* STATS */}
              <div className="emp-stats-row">
                {[
                  { label: "Total Weeks", sub: "This Year", value: totalWeeks, pct: submittedPct, cls: "blue", iconName: "Calendar" },
                  { label: "Submitted", sub: `${submittedPct}%`, value: submittedCount, pct: submittedPct, cls: "green", iconName: "CheckCircle" },
                  { label: "Pending", sub: `${pendingPct}%`, value: pendingCount, pct: pendingPct, cls: "amber", iconName: "Clock" },
                  { label: "Leave Balance", sub: "Days", value: remainingLeaveDays, pct: leavePct, cls: "amber", iconName: "LeaveCalendar" },
                ].map((s, i) => (
                  <motion.div key={s.label} className="emp-stat-card"
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + i * 0.06, duration: 0.3 }}
                    onClick={() => { if (s.label === "Pending") handleNavClick("approvals"); if (s.label === "Leave Balance") handleNavClick("leave"); }}
                    style={{ cursor: s.label === "Pending" || s.label === "Leave Balance" ? "pointer" : "default" }}
                  >
                    <div className={`emp-stat-icon-wrap ${s.cls}`}>
                      {s.iconName === "Calendar" && <Icon.Calendar size={18} color="#2563EB" />}
                      {s.iconName === "CheckCircle" && <Icon.CheckCircle size={18} color="#10B981" />}
                      {s.iconName === "Clock" && <Icon.Clock size={18} color="#F59E0B" />}
                      {s.iconName === "LeaveCalendar" && <Icon.Calendar size={18} color="#F59E0B" />}
                    </div>
                    <div className="emp-stat-content">
                      <div className="emp-stat-value">{s.value}</div>
                      <div className="emp-stat-label">{s.label}</div>
                      <div className="emp-stat-sub">{s.sub}</div>
                      <div className="emp-stat-progress">
                        <div className={`emp-stat-progress-bar ${s.cls}`} style={{ width: `${Math.max(s.pct, 4)}%` }} />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* TIMESHEET TABLE - removed from dashboard, available in Timesheets tab */}
              <motion.div className="emp-ts-section" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.35 }}
                style={{ background: "#fff", border: "1px solid #E2EEFF", borderRadius: 14, padding: "20px 24px", boxShadow: "0 1px 4px rgba(37,99,235,0.05)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <h3 style={{ fontSize: "0.9375rem", fontWeight: 700, color: "#0F172A", margin: 0 }}>Timesheet Overview</h3>
                  <button className="emp-ts-toolbar-btn" onClick={() => handleNavClick("timesheets")} style={{ fontSize: "0.8rem", fontWeight: 600, color: "#2563EB", background: "none", border: "none", cursor: "pointer" }}>View All Timesheets →</button>
                </div>
              </motion.div>

              {/* CALENDAR */}
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.35 }}>
                <CalendarView submittedTimesheets={submittedTimesheets} approvedLeaveDates={approvedLeaveDates} holidayDates={holidayDates} />
              </motion.div>

              {/* FEATURE CAROUSEL - removed */}
            </div>

            {/* RIGHT PANEL */}
            <div className="emp-right">
              {/* <motion.div initial={{opacity:0,x:16}} animate={{opacity:1,x:0}} transition={{delay:0.15,duration:0.3}}>
            <button type="button"
              style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",background:"#fff",border:"1px solid #E2EEFF",borderRadius:10,cursor:"pointer",fontFamily:"var(--font-sans)",fontSize:"0.8125rem",fontWeight:600,color:"#1E293B",width:"100%",justifyContent:"space-between"}}
              onClick={()=>handleNavClick("notifications")} aria-label="Notifications">
              <span style={{display:"flex",alignItems:"center",gap:7}}><Icon.Bell size={16} color="#2563EB"/> Notifications</span>
              {notifications.length>0&&<span style={{background:"#EF4444",color:"#fff",fontSize:"0.65rem",fontWeight:700,padding:"1px 6px",borderRadius:999}}>{notifications.length}</span>}
            </button>
          </motion.div> */}

              <motion.div className="emp-deadline-card" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2, duration: 0.3 }}>
                <div className="emp-deadline-label"><Icon.Clock size={12} color="#F59E0B" /> UPCOMING DEADLINE</div>
                <div className="emp-deadline-title">Submit this week's timesheet</div>
                <div className="emp-deadline-days">{daysToFriday} Days Left</div>
                <div className="emp-deadline-due">Due on {nextFriday}</div>
                <button className="emp-deadline-btn" onClick={() => {
                  const thisWeek = sundays.find(s => s === thisWeekKey);
                  if (thisWeek) {
                    const st = submittedTimesheets[thisWeek]?.status || "Not Submitted";
                    if (st === "Not Submitted" || st === "Rejected") { setSelectedWeek(thisWeek); setShowAddModal(true); }
                    else handleNavClick("timesheets");
                  } else handleNavClick("timesheets");
                }}>View Timesheet</button>
              </motion.div>

              <motion.div className="emp-progress-card" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25, duration: 0.3 }}>
                <h4>This Week Progress</h4>
                <div className="emp-progress-circle-wrap">
                  <CircularProgress pct={weekPct} />
                  <div className="emp-progress-legend">
                    <div className="emp-progress-item">
                      <span className="emp-progress-item-label">{fmtHours(thisWeekHrs)}</span>
                      <span className="emp-progress-item-val blue">Logged Hours</span>
                    </div>
                    <div className="emp-progress-item">
                      <span className="emp-progress-item-label">{fmtHours(Math.max(40 - thisWeekHrs, 0))}</span>
                      <span className="emp-progress-item-val gray">Remaining Hours</span>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div className="emp-activity-card" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.32, duration: 0.3 }}>
                <div className="emp-activity-header">
                  <h4>Recent Activity</h4>
                </div>
                <div className="emp-activity-list">
                  {recentActivity.length === 0
                    ? <p style={{ fontSize: "0.78rem", color: "#94A3B8" }}>No recent activity.</p>
                    : recentActivity.map((a, i) => (
                      <div key={i} className="emp-activity-item">
                        <div className={`emp-activity-icon ${a.icon}`}>
                          {a.iconName === "FileText" && <Icon.FileText size={14} color={a.icon === "blue" ? "#2563EB" : "#10B981"} />}
                          {a.iconName === "CheckCircle" && <Icon.CheckCircle size={14} color="#10B981" />}
                        </div>
                        <div className="emp-activity-text"><h5>{a.title}</h5><p>{a.time}</p></div>
                      </div>
                    ))}
                </div>
              </motion.div>
            </div>
          </>
        )}
      </div>

      {/* ══ NOTIFICATIONS PORTAL ══ */}
      {showNotifications && createPortal(
        <>
          <button type="button" style={{ position: "fixed", inset: 0, zIndex: 1500, background: "transparent", border: "none", cursor: "default" }}
            onClick={() => setShowNotifications(false)} aria-label="Close notifications" />
          <motion.div
            style={{ position: "fixed", top: "calc(var(--header-height) + 8px)", right: 16, width: 340, background: "#fff", border: "1.5px solid #E2EEFF", borderRadius: 14, boxShadow: "0 8px 32px rgba(37,99,235,0.14)", zIndex: 1600, overflow: "hidden" }}
            initial={{ opacity: 0, y: -8, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid #F1F5F9", fontSize: "0.875rem", fontWeight: 700, color: "#1E293B" }}>
              <span>Notifications</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {notifications.length > 0 && <button style={{ fontSize: "0.75rem", color: "#2563EB", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }} onClick={markAllRead}>Mark all read</button>}
                <button style={{ width: 24, height: 24, borderRadius: 6, background: "transparent", border: "1px solid #E2EEFF", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                  onClick={() => setShowNotifications(false)} aria-label="Close"><Icon.X size={11} color="#64748B" /></button>
              </div>
            </div>
            {notifications.length === 0
              ? <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "36px 20px", color: "#64748B", fontSize: "0.84rem" }}><Icon.CheckCircle size={24} color="#86efac" /> You're all caught up!</div>
              : <ul style={{ listStyle: "none", maxHeight: 320, overflowY: "auto" }}>
                {notifications.map(n => (
                  <li key={n.id} style={{ display: "flex", flexDirection: "column", gap: 4, padding: "12px 16px", borderBottom: "1px solid #F8FBFF", borderLeft: `3px solid ${n.type?.includes("APPROVED") ? "#10B981" : "#EF4444"}` }}>
                    <span style={{ fontSize: "0.8125rem", fontWeight: 500, color: "#1E293B" }}>{n.message}</span>
                    <span style={{ fontSize: "0.72rem", color: "#94A3B8" }}>{n.createdAt ? new Date(n.createdAt).toLocaleDateString() : ""}</span>
                  </li>
                ))}
              </ul>}
          </motion.div>
        </>,
        document.body
      )}

      {/* ══ SUBMIT TIMESHEET MODAL ══ */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 20 }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            role="dialog" aria-modal="true" aria-label="Create timesheet">
            <motion.div style={{ background: "#fff", border: "1px solid #E2EEFF", borderRadius: 18, boxShadow: "0 16px 48px rgba(37,99,235,0.14)", width: "100%", maxWidth: 700, maxHeight: "88vh", overflow: "hidden", display: "flex", flexDirection: "column" }}
              initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 20 }} transition={{ duration: 0.25 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 24px", borderBottom: "1px solid #F1F5F9" }}>
                <h3 style={{ fontSize: "1rem", fontWeight: 700, color: "#1E293B" }}>
                  {(() => { const st = submittedTimesheets[selectedWeek]?.status || "Not Submitted"; return st === "Not Submitted" ? "Create Timesheet" : st === "Rejected" ? "Edit & Resubmit" : "View Timesheet"; })()}
                </h3>
                <button style={{ width: 30, height: 30, borderRadius: 8, background: "transparent", border: "1px solid #E2EEFF", cursor: "pointer", fontSize: "1.1rem", color: "#64748B", display: "flex", alignItems: "center", justifyContent: "center" }}
                  onClick={() => setShowAddModal(false)} aria-label="Close">×</button>
              </div>
              <div style={{ overflowY: "auto", padding: 24 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
                  <label htmlFor="week-sel" style={{ fontSize: "0.8125rem", fontWeight: 600, color: "#1E293B" }}>Select Week:</label>
                  <select id="week-sel" value={selectedWeek} onChange={e => setSelectedWeek(e.target.value)}
                    style={{ padding: "8px 12px", border: "1.5px solid #E2EEFF", borderRadius: 8, fontSize: "0.84rem", fontFamily: "var(--font-sans)", color: "#1E293B", background: "#F8FBFF", outline: "none" }}>
                    {sundays.map(s => {
                      const st = submittedTimesheets[s]?.status || "Not Submitted";
                      return <option key={s} value={s}>{dayjs(s).format("MMM D")} – {dayjs(s).add(6, "day").format("MMM D, YYYY")} — {st}</option>;
                    })}
                  </select>
                </div>
                {selectedWeek && <TimesheetDropdown weekStart={selectedWeek} existingData={submittedTimesheets[selectedWeek]}
                  username={username} startDate={startDate} approvedLeaveDates={approvedLeaveDates} holidayDates={holidayDates} autoOpen={true}
                  clientNotesEnabled={clientNotesEnabled}
                  fetchSubmittedTimesheets={() => { fetchSubmittedTimesheets(); setShowAddModal(false); }} />}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══ LEAVE MODAL ══ */}
      <AnimatePresence>
        {showLeaveModal && (
          <motion.div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 20 }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            role="dialog" aria-modal="true" onClick={() => !leaveSubmitting && setShowLeaveModal(false)}>
            <motion.div style={{ background: "#fff", border: "1px solid #E2EEFF", borderRadius: 18, boxShadow: "0 16px 48px rgba(37,99,235,0.14)", width: "100%", maxWidth: 520, overflow: "hidden" }}
              initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 20 }} transition={{ duration: 0.25 }}
              onClick={e => e.stopPropagation()}>
              <div style={{ background: "linear-gradient(135deg,#1e40af,#2563eb)", padding: "28px 28px 24px", position: "relative" }}>
                <h2 style={{ fontSize: "1.125rem", fontWeight: 700, color: "#fff", marginBottom: 4 }}>Apply for Leave</h2>
                <p style={{ fontSize: "0.8125rem", color: "rgba(255,255,255,0.72)" }}>Submit your leave request</p>
                <button style={{ position: "absolute", top: 16, right: 16, width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.18)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                  onClick={() => setShowLeaveModal(false)} disabled={leaveSubmitting} aria-label="Close"><Icon.X size={12} color="#fff" /></button>
              </div>
              {leaveSuccess ? (
                <div style={{ padding: "48px 28px", display: "flex", flexDirection: "column", alignItems: "center", gap: 14, textAlign: "center" }}>
                  <Icon.CheckCircle size={48} color="#10B981" strokeWidth={1.5} />
                  <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#10B981" }}>Leave Request Submitted!</h3>
                  <p style={{ fontSize: "0.875rem", color: "#64748B" }}>Your request has been sent for approval.</p>
                </div>
              ) : (
                <form style={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: 16 }} onSubmit={handleLeaveSubmit}>
                  <div>
                    <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#1E293B", display: "block", marginBottom: 8 }}>Leave Type</span>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      {LEAVE_TYPES.map(lt => (
                        <label key={lt.value} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderRadius: 10, border: `1.5px solid ${leaveForm.leaveType === lt.value ? lt.color : "#E2EEFF"}`, background: leaveForm.leaveType === lt.value ? `${lt.color}12` : "#F8FBFF", fontSize: "0.8125rem", fontWeight: leaveForm.leaveType === lt.value ? 600 : 500, cursor: "pointer", color: leaveForm.leaveType === lt.value ? lt.color : "#475569", transition: "all 150ms" }}>
                          <input type="radio" name="leaveType" value={lt.value} checked={leaveForm.leaveType === lt.value}
                            onChange={e => setLeaveForm(p => ({ ...p, leaveType: e.target.value }))} required style={{ display: "none" }} />
                          {lt.label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "end", gap: 8 }}>
                    <div><label htmlFor="ls" style={{ fontSize: "0.78rem", fontWeight: 600, color: "#1E293B", display: "block", marginBottom: 5 }}>From</label>
                      <input id="ls" type="date" value={leaveForm.startDate} min={startDate || undefined}
                        onChange={e => setLeaveForm(p => ({ ...p, startDate: e.target.value }))} required
                        style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #E2EEFF", borderRadius: 8, fontSize: "0.84rem", fontFamily: "var(--font-sans)", outline: "none" }} /></div>
                    <div style={{ paddingBottom: 10, color: "#94A3B8", textAlign: "center" }}>→</div>
                    <div><label htmlFor="le" style={{ fontSize: "0.78rem", fontWeight: 600, color: "#1E293B", display: "block", marginBottom: 5 }}>To</label>
                      <input id="le" type="date" value={leaveForm.endDate} min={leaveForm.startDate || startDate || undefined}
                        onChange={e => setLeaveForm(p => ({ ...p, endDate: e.target.value }))} required
                        style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #E2EEFF", borderRadius: 8, fontSize: "0.84rem", fontFamily: "var(--font-sans)", outline: "none" }} /></div>
                  </div>
                  {leaveForm.startDate && leaveForm.endDate && (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 999, fontSize: "0.8125rem", fontWeight: 600, color: "#2563EB" }}>
                      {dayjs(leaveForm.endDate).diff(dayjs(leaveForm.startDate), "day") + 1} day(s) requested
                    </div>
                  )}
                  <div><label htmlFor="lr" style={{ fontSize: "0.78rem", fontWeight: 600, color: "#1E293B", display: "block", marginBottom: 5 }}>
                    Reason {leaveForm.leaveType === "other" && <span style={{ color: "#EF4444" }}>*</span>}
                  </label>
                    <textarea id="lr" value={leaveForm.reason} onChange={e => setLeaveForm(p => ({ ...p, reason: e.target.value }))}
                      required={leaveForm.leaveType === "other"} rows={3}
                      placeholder={leaveForm.leaveType === "other" ? "Required — briefly describe the reason..." : "Briefly describe the reason..."}
                      style={{ width: "100%", padding: "10px 12px", border: `1.5px solid ${leaveForm.leaveType === "other" && !leaveForm.reason?.trim() ? "#FCA5A5" : "#E2EEFF"}`, background: leaveForm.leaveType === "other" && !leaveForm.reason?.trim() ? "#FFF8F8" : "#fff", borderRadius: 8, fontSize: "0.84rem", fontFamily: "var(--font-sans)", outline: "none", resize: "vertical", minHeight: 80 }} /></div>
                  <button type="submit" disabled={leaveSubmitting || (leaveForm.leaveType === "other" && !leaveForm.reason?.trim())}
                    style={{ width: "100%", padding: 12, background: "#2563EB", color: "#fff", border: "none", borderRadius: 10, fontSize: "0.9375rem", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "var(--font-sans)", opacity: leaveSubmitting ? 0.65 : 1 }}>
                    {leaveSubmitting ? <span style={{ display: "inline-block", width: 16, height: 16, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} /> : <>Submit Request →</>}
                  </button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══ CONTACT SUPPORT MODAL ══ */}
      <AnimatePresence>
        {showSupportModal && (
          <motion.div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 20 }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setShowSupportModal(false)}>
            <motion.div style={{ background: "#fff", border: "1px solid #E2EEFF", borderRadius: 18, boxShadow: "0 16px 48px rgba(37,99,235,0.14)", width: "100%", maxWidth: 460, overflow: "hidden" }}
              initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 20 }} transition={{ duration: 0.25 }}
              onClick={e => e.stopPropagation()}>
              <div style={{ background: "linear-gradient(135deg,#1e40af,#2563eb)", padding: "24px 28px", position: "relative" }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
                  <Icon.AlertCircle size={22} color="#fff" />
                </div>
                <h2 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: 4 }}>Contact Support</h2>
                <p style={{ fontSize: "0.8125rem", color: "rgba(255,255,255,0.72)" }}>Send a message to the WorkForce team</p>
                <button style={{ position: "absolute", top: 14, right: 14, width: 26, height: 26, borderRadius: 6, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.18)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                  onClick={() => setShowSupportModal(false)} aria-label="Close"><Icon.X size={11} color="#fff" /></button>
              </div>
              <div style={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ padding: "12px 16px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 10 }}>
                  <p style={{ fontSize: "0.8rem", color: "#1E3A8A", fontWeight: 600, marginBottom: 4 }}>📧 Email Support</p>
                  <p style={{ fontSize: "0.8rem", color: "#2563EB" }}>support@skypa.com</p>
                </div>
                <div style={{ padding: "12px 16px", background: "#ECFDF5", border: "1px solid #A7F3D0", borderRadius: 10 }}>
                  <p style={{ fontSize: "0.8rem", color: "#065F46", fontWeight: 600, marginBottom: 4 }}>⏰ Support Hours</p>
                  <p style={{ fontSize: "0.8rem", color: "#059669" }}>Monday – Friday, 9 AM – 6 PM IST</p>
                </div>
                <div>
                  <label style={{ fontSize: "0.8rem", fontWeight: 600, color: "#1E293B", display: "block", marginBottom: 6 }}>Your Message</label>
                  <textarea value={supportMsg} onChange={e => setSupportMsg(e.target.value)} rows={4}
                    placeholder="Describe your issue or question..."
                    style={{ width: "100%", padding: "10px 12px", border: "1.5px solid #E2EEFF", borderRadius: 8, fontSize: "0.84rem", fontFamily: "var(--font-sans)", outline: "none", resize: "vertical" }} />
                </div>
                <button type="button"
                  style={{ width: "100%", padding: "11px", background: "#2563EB", color: "#fff", border: "none", borderRadius: 10, fontSize: "0.9rem", fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-sans)" }}
                  onClick={() => { showToast("Message sent! Our team will respond within 24 hours.", "success"); setSupportMsg(""); setShowSupportModal(false); }}>
                  Send Message
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══ ADD TASK MODAL (from homepage) ══ */}
      <AddTaskModal
        isOpen={showAddTaskModal}
        onClose={() => setShowAddTaskModal(false)}
        showToast={showToast}
        onSuccess={() => {
          setTasksVersion(v => v + 1);
        }}
      />

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* ══ TOAST ══ */}
      <AnimatePresence>
        {toast && (
          <motion.div
            key="toast"
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.22 }}
            style={{
              position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)",
              zIndex: 9999, display: "flex", alignItems: "center", gap: 10,
              padding: "13px 20px", borderRadius: 12,
              background: toast.type === "error" ? "#FEF2F2" : toast.type === "success" ? "#ECFDF5" : "#EFF6FF",
              border: `1px solid ${toast.type === "error" ? "#FECACA" : toast.type === "success" ? "#A7F3D0" : "#BFDBFE"}`,
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              color: toast.type === "error" ? "#DC2626" : toast.type === "success" ? "#059669" : "#2563EB",
              fontSize: "0.875rem", fontWeight: 600, maxWidth: 420, pointerEvents: "none",
            }}
          >
            {toast.type === "error" && <Icon.AlertCircle size={16} color="#DC2626" />}
            {toast.type === "success" && <Icon.CheckCircle size={16} color="#059669" />}
            {toast.type === "info" && <Icon.AlertCircle size={16} color="#2563EB" />}
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EmployeeHome;
